import {commonControllers} from '@mintdata-wsp/common/controllers/CommonControllers';
import {ResolvedVisualComponent} from '@mintdata-wsp/common/state/ui/visualDesigner/artifacts/ResolvedVisualComponent';
import {VisualComponent} from '@mintdata-wsp/common/state/ui/visualDesigner/artifacts/VisualComponent';
import {DesignStateFromJsonCtx} from '@mintdata-wsp/common/state/ui/visualDesigner/DesignState';
import {BindingDirection, PropBinding} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/binding/Binding';
import {Box} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/component/Box';
import {LayoutUtils} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/component/LayoutUtils';
import {LogicBridge} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/component/LogicBridge';
import {ChildLayoutModel} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/layouts/ChildLayoutModel';
import {LayoutModel} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/layouts/LayoutModel';
import {PositioningType} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/layouts/PositioningType';
import {DnDModel} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/dnd/DnDModel';
import {EditorPartState} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/EditorPartState';
import {EventProp, IEventPropSnapshot} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/EventProp';
import {LoaderModel} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/loader/LoaderModel';
import {OpacityModel} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/opacity/OpacityModel';
import {PerspectiveModel} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/perspective/PerspectiveModel';
import {
    IProperty,
    IPropSnapshot,
    Prop,
    PropertyGroup
} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/Prop';
import {PropConfig} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/PropConfig';
import {Repeater} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/repeater/Repeater';
import {RepeaterModel} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/repeater/RepeaterModel';
import {RepeaterModelFieldPaths} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/repeater/RepeaterModelFieldPaths';
import {RotationModel} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/rotation/RotationModel';
import {ScaleModel} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/scale/ScaleModel';
import {SkewModel} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/skew/SkewModel';
import {SystemPropsConfigs} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/SystemPropsConfigs';
import {ComponentConfig} from '@mintdata/mint-kit/api/decorators/Component';
import {EventMap} from '@mintdata/mint-kit/api/events/EventMap';
import {EventSpec} from '@mintdata/mint-kit/api/events/EventSpec';
import {EventPropName} from '@mintdata/mint-kit/api/events/EventType';
import {area, mk} from '@mintdata/mint-kit/api/log/mk';
import {BindingId, EventPropId, PropId} from '@mintdata/mint-kit/api/models/Identifiers';
import {LayoutHorizontal} from '@mintdata/mint-kit/api/models/LayoutHorizontal';
import {LayoutVertical} from '@mintdata/mint-kit/api/models/LayoutVertical';
import {PositiveUnits} from '@mintdata/mint-kit/api/models/PositiveUnits';
import {Units} from '@mintdata/mint-kit/api/models/Units';
import {BindPath} from '@mintdata/mint-kit/api/properties/BindPath';
import {ContainerPropertyConfig} from '@mintdata/mint-kit/api/properties/ContainerPropertyConfig';
import {IPropertyConfig} from '@mintdata/mint-kit/api/properties/PropertyConfig';
import {UserInputValue} from '@mintdata/mint-kit/api/properties/PropertyEditorPart';
import {PropName} from '@mintdata/mint-kit/api/properties/PropertySpec';
import {PropPath} from '@mintdata/mint-kit/api/properties/PropPath';
import {ValidationResult} from '@mintdata/mint-kit/api/properties/ValidationResult';
import {Type} from '@mintdata/mint-kit/api/types/Type';
import {IValue} from '@mintdata/mint-kit/api/types/Value';
import {ABoolean} from '@mintdata/mint-kit/api/values/ABoolean';
import {AString} from '@mintdata/mint-kit/api/values/AString';
import {FramePath} from '@mintdata/mint-kit/api/values/frames/Frame';
import {serialize, SKIP} from '@mintdata/mint-kit/impl/util/serializr/src/serializr';
import _get from 'lodash/get';
import {action, computed, observable} from 'mobx';
import {createTransformer, ITransformer} from 'mobx-utils';

export interface IComponentDescSnapshot
{
    linkNameToProp:string;
    _baseProperties:IPropsSnapshot;
    _properties:IPropsSnapshot;
    _layoutProperties:IPropsSnapshot;
    _eventProps:IEventPropsSnapshot;
    _baseEventProps:IEventPropsSnapshot;
}

export interface IPropsSnapshot
{
    [propName:string]:IPropSnapshot;
}

export interface IEventPropsSnapshot
{
    [eventPropName:string]:IEventPropSnapshot;
}

export interface IPropsValues
{
    [propName:string]:any;
}

export interface SelfAlignedBounds
{
    top?:string;
    left?:string;
    right?:string;
    bottom?:string;

    width?:string;
    height?:string;
}

class PropPathValidationResult
{
    constructor(public readonly propPath:PropPath, public readonly isValid:boolean)
    {
    }
}

class LogicBridgeValidationResult
{
    constructor(public readonly results:Array<PropPathValidationResult>)
    {
    }

    public get isValid():boolean
    {
        return this.results.every(prop => prop.isValid);
    }

    static valid(propPath:PropPath)
    {
        return new LogicBridgeValidationResult([new PropPathValidationResult(propPath, true)]);
    }
}

export class ComponentDesc
{
    public box:Box;

    @observable
    public linkNameToProp:string;

    @observable
    private _baseProperties:Map<PropId, Prop<any>>;

    @observable
    private _properties:Map<PropId, Prop<any>>;

    @observable
    private _layoutProperties:Map<PropId, Prop<any>>;

    @observable
    private _eventProps:Map<EventPropId, EventProp>;

    @observable
    private _baseEventProps:Map<EventPropId, EventProp>;

    private _repeaterPropPath:PropPath;

    private _logicBridge:LogicBridge;

    private constructor()
    {
        this._repeaterPropPath = new PropPath([RepeaterModelFieldPaths.VALUES_FIELD]);
    }

    public static create(box:Box):ComponentDesc
    {
        const componentDesc:ComponentDesc = new ComponentDesc();

        componentDesc.box = box;
        box.componentDesc = componentDesc;

        const {resolvedComponent} = box.component.ref;
        componentDesc.linkNameToProp = resolvedComponent.componentConfig.linkNameToProp || '';

        componentDesc._baseProperties = ComponentDesc.createProps(
            box,
            SystemPropsConfigs.basePropertiesConfigs,
            PropertyGroup.Base
        );

        componentDesc._properties = ComponentDesc.createProps(
            box,
            resolvedComponent.propertiesConfigs,
            PropertyGroup.Component
        );

        componentDesc._layoutProperties = ComponentDesc.createProps(
            box,
            resolvedComponent.layoutPropertiesConfigs,
            PropertyGroup.Layout,
            !componentDesc.componentConfig.supportsChildren
        );

        componentDesc._baseEventProps = ComponentDesc.createEventProps(
            box,
            SystemPropsConfigs.baseEventsMap
        );

        componentDesc._eventProps = ComponentDesc.createEventProps(
            box,
            resolvedComponent.eventMap
        );

        componentDesc._logicBridge = new LogicBridge(componentDesc);

        return componentDesc;
    }

    public get visualComponent():VisualComponent
    {
        return this.box.component;
    }

    public get resolvedVisualComponent():ResolvedVisualComponent
    {
        return this.visualComponent
            && this.visualComponent.ref
            && this.visualComponent.ref.resolvedComponent;
    }

    public get componentCtor():any
    {
        return this.resolvedVisualComponent
            && this.resolvedVisualComponent.componentCtor;
    }

    public get componentConfig():ComponentConfig<any, any>
    {
        return this.resolvedVisualComponent
            && this.resolvedVisualComponent.componentConfig;
    }

    public get propertiesConfigs():Map<PropName, PropConfig<any>>
    {
        return this.resolvedVisualComponent
            && this.resolvedVisualComponent.propertiesConfigs;
    }

    public get layoutPropertiesConfigs():Map<PropName, PropConfig<any>>
    {
        return this.resolvedVisualComponent
            && this.resolvedVisualComponent.layoutPropertiesConfigs;
    }

    public get containerPropertyConfig():ContainerPropertyConfig
    {
        return this.resolvedVisualComponent
            && this.resolvedVisualComponent.containerPropertyConfig;
    }

    public get eventMap():EventMap
    {
        return this.resolvedVisualComponent
            && this.resolvedVisualComponent.eventMap;
    }

    public get logicBridge():LogicBridge
    {
        return this._logicBridge;
    }

    @computed
    public get baseProperties():Map<PropName, Prop<any>>
    {
        return this._getPropertiesByNameMap(this._baseProperties);
    }

    @computed
    public get properties():Map<PropName, Prop<any>>
    {
        return this._getPropertiesByNameMap(this._properties);
    }

    @computed
    public get layoutProperties():Map<PropName, Prop<any>>
    {
        return this._getPropertiesByNameMap(this._layoutProperties);
    }

    @computed
    public get eventPropsMapByName():Map<EventPropName, EventProp>
    {
        const eventProperties:Map<EventPropName, EventProp> = new Map<EventPropName, EventProp>();

        this._eventProps.forEach((eventProp:EventProp) => eventProperties.set(eventProp.name, eventProp));

        return eventProperties;
    }

    @computed
    public get baseEventPropsMapByName():Map<EventPropName, EventProp>
    {
        const baseEventProperties:Map<EventPropName, EventProp> = new Map<EventPropName, EventProp>();

        this._baseEventProps.forEach((baseEventProp:EventProp) => baseEventProperties.set(baseEventProp.name, baseEventProp));

        return baseEventProperties;
    }

    public get autofocusProp():Prop<ABoolean>
    {
        if( this.baseProperties && this.baseProperties.has(SystemPropsConfigs.AUTOFOCUS_PROP_NAME) )
            return this.baseProperties.get(SystemPropsConfigs.AUTOFOCUS_PROP_NAME);

        return null;
    }

    @computed
    public get propsValues():IPropsValues
    {
        const repeater:Repeater = Repeater.default(this);
        const propsValues = this._getPropsValues(this._properties, repeater);

        return this._logicBridge.populateEventHandlers(repeater, propsValues);
    }

    @computed
    public get pendingValues():IPropsValues
    {
        const pendingValues = {};

        this._properties.forEach(prop =>
            pendingValues[prop.name] = prop.pendingValue
        );

        return pendingValues;
    }

    @computed
    public get name():AString
    {
        if( this.linkNameToProp )
        {
            const name:AString = _get(this.propsValues, this.linkNameToProp, null);

            if( name )
                return name;
        }

        if( this.nameProp )
            return this.nameProp.value;

        return null;
    }

    public get nameProp():Prop<AString>
    {
        if( this.baseProperties && this.baseProperties.has(SystemPropsConfigs.NAME_PROP_NAME) )
            return this.baseProperties.get(SystemPropsConfigs.NAME_PROP_NAME);

        return null;
    }

    @computed
    public get category():AString
    {
        if( this.baseProperties && this.baseProperties.has(SystemPropsConfigs.CATEGORY_PROP_NAME) )
            return this.baseProperties.get(SystemPropsConfigs.CATEGORY_PROP_NAME).value;

        return null;
    }

    public get repeaterProp():Prop<RepeaterModel>
    {
        if( this.baseProperties && this.baseProperties.has(SystemPropsConfigs.REPEATER_PROP_NAME) )
            return this.baseProperties.get(SystemPropsConfigs.REPEATER_PROP_NAME);

        return null;
    }

    public get visibilityProp():Prop<ABoolean>
    {
        if( this.baseProperties && this.baseProperties.has(SystemPropsConfigs.VISIBILITY_PROP_NAME) )
            return this.baseProperties.get(SystemPropsConfigs.VISIBILITY_PROP_NAME);

        return null;
    }

    public get lockProp():Prop<ABoolean>
    {
        if( this.baseProperties && this.baseProperties.has(SystemPropsConfigs.LOCK_PROP_NAME) )
            return this.baseProperties.get(SystemPropsConfigs.LOCK_PROP_NAME);

        return null;
    }

    public get loaderProp():Prop<LoaderModel>
    {
        if( this.baseProperties && this.baseProperties.has(SystemPropsConfigs.LOADER_PROP_NAME) )
            return this.baseProperties.get(SystemPropsConfigs.LOADER_PROP_NAME);

        return null;
    }

    public get rotationProp():Prop<RotationModel>
    {
        if( this.layoutProperties && this.layoutProperties.has(SystemPropsConfigs.ROTATION_PROP_NAME) )
            return this.layoutProperties.get(SystemPropsConfigs.ROTATION_PROP_NAME);

        return null;
    }

    public get scaleProp():Prop<ScaleModel>
    {
        if( this.layoutProperties && this.layoutProperties.has(SystemPropsConfigs.SCALE_PROP_NAME) )
            return this.layoutProperties.get(SystemPropsConfigs.SCALE_PROP_NAME);

        return null;
    }

    public get opacityProp():Prop<OpacityModel>
    {
        if( this.baseProperties && this.baseProperties.has(SystemPropsConfigs.OPACITY_PROP_NAME) )
            return this.baseProperties.get(SystemPropsConfigs.OPACITY_PROP_NAME);

        return null;
    }

    public get skewProp():Prop<SkewModel>
    {
        if( this.layoutProperties && this.layoutProperties.has(SystemPropsConfigs.SKEW_PROP_NAME) )
            return this.layoutProperties.get(SystemPropsConfigs.SKEW_PROP_NAME);

        return null;
    }

    public get perspectiveProp():Prop<PerspectiveModel>
    {
        if( this.layoutProperties && this.layoutProperties.has(SystemPropsConfigs.PERSPECTIVE_PROP_NAME) )
            return this.layoutProperties.get(SystemPropsConfigs.PERSPECTIVE_PROP_NAME);

        return null;
    }

    public get dndProp():Prop<DnDModel>
    {
        if( this.baseProperties && this.baseProperties.has(SystemPropsConfigs.DND_PROP_NAME) )
            return this.baseProperties.get(SystemPropsConfigs.DND_PROP_NAME);

        return null;
    }

    public get onDragStartedEventProp():EventProp
    {
        return this.getEventPropByName(SystemPropsConfigs.DND_ON_DRAG_STARTED_EVENT_NAME);
    }

    public get onDragInProgressEventProp():EventProp
    {
        return this.getEventPropByName(SystemPropsConfigs.DND_ON_DRAG_IN_PROGRESS_EVENT_NAME);
    }

    public get onDropEventProp():EventProp
    {
        return this.getEventPropByName(SystemPropsConfigs.DND_ON_DROP_EVENT_NAME);
    }

    @computed
    public get isRepeaterBound():boolean
    {
        return this.repeaterProp
            && this.repeaterProp.isBoundPath(this._repeaterPropPath);
    }

    @computed
    public get repeatDirection():BindingDirection
    {
        if( this.isRepeaterBound )
        {
            const bindingId:BindingId = this.repeaterProp.getBindingId(this._repeaterPropPath);

            if( bindingId )
            {
                const repeaterBinding = commonControllers.bindings.getPropBinding(bindingId);
                if( repeaterBinding )
                    return repeaterBinding.direction;
            }
        }

        return BindingDirection.Down;
    }

    @computed
    public get repeaterPathInFrame():FramePath
    {
        if( this.isRepeaterBound )
        {
            const bindingId:BindingId = this.repeaterProp.getBindingId(this._repeaterPropPath);

            if( bindingId )
            {
                const {bindings, spreadsheets: {namedRanges}} = commonControllers;
                const repeaterBinding = bindings.getPropBinding(bindingId);
                if( repeaterBinding )
                    return repeaterBinding.range.framePath;
            }
        }

        return null;
    }

    @computed
    public get isRepeated():boolean
    {
        return this.isRepeaterBound
            || (this.repeaterProp && this.repeaterProp.value
                && this.repeaterProp.value.count && this.repeaterProp.value.count.value > 1);
    }

    @computed
    public get layout():LayoutModel
    {
        if( this.layoutProp )
            return this.layoutProp.value;

        return null;
    }

    @computed
    public get childLayout():ChildLayoutModel
    {
        if( this.childLayoutProp )
            return this.childLayoutProp.value;

        return null;
    }

    @computed
    public get layoutLeft():Units
    {
        if( this.layoutLeftProp )
            return this.layoutLeftProp.value;

        return null;
    }

    @computed
    public get layoutTop():Units
    {
        if( this.layoutTopProp )
            return this.layoutTopProp.value;

        return null;
    }

    @computed
    public get layoutRight():Units
    {
        if( this.layoutRightProp )
            return this.layoutRightProp.value;

        return null;
    }

    @computed
    public get layoutBottom():Units
    {
        if( this.layoutBottomProp )
            return this.layoutBottomProp.value;

        return null;
    }

    @computed
    public get layoutWidth():PositiveUnits
    {
        if( this.layoutWidthProp )
            return this.layoutWidthProp.value;

        return null;
    }

    @computed
    get layoutHeight():PositiveUnits
    {
        if( this.layoutHeightProp )
            return this.layoutHeightProp.value;

        return null;
    }

    @computed
    public get layoutVertical():LayoutVertical
    {
        if( this.layoutVerticalProp )
            return this.layoutVerticalProp.value;

        return null;
    }

    @computed
    public get layoutHorizontal():LayoutHorizontal
    {
        if( this.layoutHorizontalProp )
            return this.layoutHorizontalProp.value;

        return null;
    }

    @computed
    public get layoutKeepProportions():ABoolean
    {
        if( this.layoutKeepProportionsProp )
            return this.layoutKeepProportionsProp.value;

        return null;
    }

    public get layoutProp():Prop<LayoutModel>
    {
        if( this.layoutProperties && this.layoutProperties.has(SystemPropsConfigs.LAYOUT_PROP_NAME) )
            return this.layoutProperties.get(SystemPropsConfigs.LAYOUT_PROP_NAME);

        return null;
    }

    public get childLayoutProp():Prop<ChildLayoutModel>
    {
        if( this.layoutProperties && this.layoutProperties.has(SystemPropsConfigs.CHILD_LAYOUT_PROP_NAME) )
            return this.layoutProperties.get(SystemPropsConfigs.CHILD_LAYOUT_PROP_NAME);

        return null;
    }

    public get layoutLeftProp():Prop<Units>
    {
        if( this.layoutProperties && this.layoutProperties.has(SystemPropsConfigs.LAYOUT_LEFT_PROP_NAME) )
            return this.layoutProperties.get(SystemPropsConfigs.LAYOUT_LEFT_PROP_NAME);

        return null;
    }

    public get layoutTopProp():Prop<Units>
    {
        if( this.layoutProperties && this.layoutProperties.has(SystemPropsConfigs.LAYOUT_TOP_PROP_NAME) )
            return this.layoutProperties.get(SystemPropsConfigs.LAYOUT_TOP_PROP_NAME);

        return null;
    }

    public get layoutRightProp():Prop<Units>
    {
        if( this.layoutProperties && this.layoutProperties.has(SystemPropsConfigs.LAYOUT_RIGHT_PROP_NAME) )
            return this.layoutProperties.get(SystemPropsConfigs.LAYOUT_RIGHT_PROP_NAME);

        return null;
    }

    public get layoutBottomProp():Prop<Units>
    {
        if( this.layoutProperties && this.layoutProperties.has(SystemPropsConfigs.LAYOUT_BOTTOM_PROP_NAME) )
            return this.layoutProperties.get(SystemPropsConfigs.LAYOUT_BOTTOM_PROP_NAME);

        return null;
    }

    public get layoutWidthProp():Prop<PositiveUnits>
    {
        if( this.layoutProperties && this.layoutProperties.has(SystemPropsConfigs.LAYOUT_WIDTH_PROP_NAME) )
            return this.layoutProperties.get(SystemPropsConfigs.LAYOUT_WIDTH_PROP_NAME);

        return null;
    }

    public get layoutHeightProp():Prop<PositiveUnits>
    {
        if( this.layoutProperties && this.layoutProperties.has(SystemPropsConfigs.LAYOUT_HEIGHT_PROP_NAME) )
            return this.layoutProperties.get(SystemPropsConfigs.LAYOUT_HEIGHT_PROP_NAME);

        return null;
    }

    public get layoutVerticalProp():Prop<LayoutVertical>
    {
        if( this.layoutProperties && this.layoutProperties.has(SystemPropsConfigs.LAYOUT_VERTICAL_PROP_NAME) )
            return this.layoutProperties.get(SystemPropsConfigs.LAYOUT_VERTICAL_PROP_NAME);

        return null;
    }

    public get layoutHorizontalProp():Prop<LayoutHorizontal>
    {
        if( this.layoutProperties && this.layoutProperties.has(SystemPropsConfigs.LAYOUT_HORIZONTAL_PROP_NAME) )
            return this.layoutProperties.get(SystemPropsConfigs.LAYOUT_HORIZONTAL_PROP_NAME);

        return null;
    }

    public get layoutKeepProportionsProp():Prop<ABoolean>
    {
        if( this.layoutProperties && this.layoutProperties.has(SystemPropsConfigs.LAYOUT_KEEP_PROPORTIONS_PROP_NAME) )
            return this.layoutProperties.get(SystemPropsConfigs.LAYOUT_KEEP_PROPORTIONS_PROP_NAME);

        return null;
    }

    public get isSelfDirected():boolean
    {
        return PositioningType.isSelfDirected(this.layout && this.layout.positioningType);
    }

    @computed
    public get containerPropValue():Array<any>
    {
        if( this.containerPropertyConfig
            && this.properties
            && this.properties.has(this.containerPropertyConfig.propertyName) )
            return this.properties.get(this.containerPropertyConfig.propertyName).value;

        return null;
    }

    @computed
    public get containerHostIds():Array<string>
    {
        if( this.containerPropertyConfig
            && this.properties
            && this.properties.has(this.containerPropertyConfig.propertyName) )
        {
            const containerItemsProp:Prop<any> = this.properties.get(this.containerPropertyConfig.propertyName);
            const containerItemsPropConfig:IPropertyConfig<any> = containerItemsProp.getPropertyConfig(
                PropPath.fromFieldPath(this.containerPropertyConfig.itemsPath)
            );

            if( !containerItemsPropConfig || !containerItemsPropConfig.spec.typeConfig.isArray )
            {
                console.error(
                    'containerHostIds: Invalid FieldPath in @ContainerProperty(spec)',
                    this.componentConfig && this.componentConfig.name,
                    this.containerPropertyConfig.propertyName
                );
                return null;
            }

            const containerItems:Array<any> = containerItemsProp.getPropValue(
                PropPath.fromFieldPath(this.containerPropertyConfig.itemsPath)
            );

            return containerItems
                ? containerItems.map(
                    (containerItem) =>
                    {
                        const containerItemValue:IValue = !BindPath.isRootPath(this.containerPropertyConfig.idPath)
                            ? _get(containerItem, this.containerPropertyConfig.idPath, null)
                            : containerItem;

                        return containerItemValue && containerItemValue.toString();
                    }
                ) : null;
        }
        else if( this.componentConfig.supportsChildren )
        {
            //container doesn't have separate containerProperty, but claims that supports children
            //   => treat this case as having exactly 1 host
            return [null];
        }

        return null;
    }

    @computed
    public get containerHostTitles():Array<string>
    {
        if( this.containerPropertyConfig
            && this.properties
            && this.properties.has(this.containerPropertyConfig.propertyName) )
        {
            const containerItemsProp:Prop<any> = this.properties.get(this.containerPropertyConfig.propertyName);
            const containerItemsPropConfig:IPropertyConfig<any> = containerItemsProp.getPropertyConfig(
                PropPath.fromFieldPath(this.containerPropertyConfig.itemsPath)
            );

            if( !containerItemsPropConfig || !containerItemsPropConfig.spec.typeConfig.isArray )
            {
                console.error(
                    'containerHostTitles: Invalid FieldPath in @ContainerProperty(spec)',
                    this.componentConfig && this.componentConfig.name,
                    this.containerPropertyConfig.propertyName
                );
                return null;
            }

            const containerItems:Array<any> = containerItemsProp.getPropValue(
                PropPath.fromFieldPath(this.containerPropertyConfig.itemsPath)
            );

            return containerItems
                ? containerItems.map(
                    (containerItem) =>
                    {
                        const containerItemValue:IValue = !BindPath.isRootPath(
                            this.containerPropertyConfig.titlePath
                            || this.containerPropertyConfig.idPath)
                            ? _get(
                                containerItem,
                                this.containerPropertyConfig.titlePath || this.containerPropertyConfig.idPath,
                                null
                            ) : containerItem;

                        return containerItemValue && containerItemValue.toString();
                    }
                ) : null;
        }
        else if( this.componentConfig.supportsChildren )
        {
            //container doesn't have separate containerProperty, but claims that supports children
            //   => treat this case as having exactly 1 host
            return [null];
        }

        return null;
    }

    @computed
    public get isMultiHostContainer():boolean
    {
        return this.containerHostIds
            && this.containerHostIds.length > 0
            && this.containerHostIds[0] !== null;
    }

    @computed
    public get bindingIds():Array<BindingId>
    {
        const bindingsIds:Array<BindingId> = [];

        this._baseProperties.forEach((prop:Prop<any>) => bindingsIds.push(...prop.bindingIds));
        this._properties.forEach((prop:Prop<any>) => bindingsIds.push(...prop.bindingIds));
        this._layoutProperties.forEach((prop:Prop<any>) => bindingsIds.push(...prop.bindingIds));
        this._eventProps.forEach((prop:EventProp) =>
        {
            if( prop.bindingId )
                return bindingsIds.push(prop.bindingId);
        });

        return bindingsIds;
    }

    public get allPropsList():Array<IProperty>
    {
        return [
            ...this.propsList,
            ...this.eventPropsList
        ];
    }

    public get propsList():Array<Prop<any>>
    {
        const props = [];

        this._baseProperties.forEach(prop => props.push(prop));
        this._properties.forEach(prop => props.push(prop));
        this._layoutProperties.forEach(prop => props.push(prop));

        return props;
    }

    public get eventPropsList():Array<EventProp>
    {
        const props = [];

        this._eventProps.forEach(prop => props.push(prop));
        this._baseEventProps.forEach(prop => props.push(prop));

        return props;
    }

    public getEventPropSpec(eventPropName:EventPropName):EventSpec
    {
        const baseEventPropSpec:EventSpec = SystemPropsConfigs.baseEventsMap.findByPropName(eventPropName);

        if( baseEventPropSpec )
            return baseEventPropSpec;

        if( !this.eventMap )
            return null;

        return this.eventMap.findByPropName(eventPropName);
    }

    public getEventPropByBindingId(bindingId:BindingId):EventProp
    {
        if( bindingId == null )
            return null;

        let eventProp:EventProp = null;

        this._eventProps.forEach((ep) =>
        {
            if( ep.bindingId == bindingId )
                eventProp = ep;
        });

        return eventProp;
    }

    public getPropByBindingId(bindingId:BindingId):Prop<any>
    {
        const searchProp = (props) =>
        {
            let foundProp = null;

            props.forEach(prop =>
            {
                if( prop.bindingsIds.find(id => id === bindingId) )
                {
                    foundProp = prop;
                }
            });

            return foundProp;
        };

        return searchProp(this._baseProperties) ||
            searchProp(this._properties) ||
            searchProp(this._layoutProperties);
    }

    public getBasePropsValues(repeater:Repeater):IPropsValues
    {
        return this._getPropsValues(this._baseProperties, repeater);
    }

    public getPropsValues(repeater:Repeater, propsValues:IPropsValues):IPropsValues
    {
        if( !propsValues )
            propsValues = this._getPropsValues(this._properties, repeater);

        return this._logicBridge.populateEventHandlers(repeater, propsValues);
    }

    public getLayoutPropsValues(repeater:Repeater):IPropsValues
    {
        return this._getPropsValues(this._layoutProperties, repeater);
    }

    public getPropById(propId:PropId):Prop<any>
    {
        return this._baseProperties.get(propId) ||
            this._properties.get(propId) ||
            this._layoutProperties.get(propId);
    }

    public getEventPropById(propId:PropId):EventProp
    {
        return this._baseEventProps.get(propId) ||
            this._eventProps.get(propId);
    }

    public getEventPropByName(eventPropName:string):EventProp
    {
        return this.baseEventPropsMapByName.has(eventPropName)
            ? this.baseEventPropsMapByName.get(eventPropName)
            : this.eventPropsMapByName.get(eventPropName);
    }

    public clone(box:Box):ComponentDesc
    {
        const cloneProps = (source:Map<PropId, Prop<any>>, target:Map<PropId, Prop<any>>) =>
        {
            source.forEach((prop:Prop<any>) =>
            {
                const clonedProp:Prop<any> = prop.clone(box);

                target.set(clonedProp.id, clonedProp);
            });
        };

        const _baseProperties:Map<PropId, Prop<any>> = new Map();
        const _properties:Map<PropId, Prop<any>> = new Map();
        const _layoutProperties:Map<PropId, Prop<any>> = new Map();
        const _baseEventProps:Map<EventPropId, EventProp> = new Map();
        const _eventProps:Map<EventPropId, EventProp> = new Map();

        cloneProps(this._baseProperties, _baseProperties);
        cloneProps(this._properties, _properties);
        cloneProps(this._layoutProperties, _layoutProperties);

        this._baseEventProps.forEach((baseEventProp:EventProp) =>
        {
            const clonedBaseEventProp:EventProp = baseEventProp.clone(box);

            _baseEventProps.set(clonedBaseEventProp.id, clonedBaseEventProp);
        });

        this._eventProps.forEach((eventProp:EventProp) =>
        {
            const clonedEventProp:EventProp = eventProp.clone(box);

            _eventProps.set(clonedEventProp.id, clonedEventProp);
        });

        const componentDesc:ComponentDesc = ComponentDesc.create(box);

        componentDesc._baseProperties = _baseProperties;
        componentDesc._properties = _properties;
        componentDesc._layoutProperties = _layoutProperties;
        componentDesc._baseEventProps = _baseEventProps;
        componentDesc._eventProps = _eventProps;

        return componentDesc;
    }

    public changePropValue(prop:Prop<any>, propPath:PropPath, userInput:UserInputValue):boolean
    {
        mk.debug(area.Events, `changePropValue:: ('${prop.name}', '${propPath.asKey()}', ${userInput})`);

        if( !prop || !propPath )
            return false;

        const propConverter = prop.getPropConverter(propPath);
        if( !propConverter )
            return false;

        const editorState = prop.getEditorPartState(propPath);
        if( !editorState )
            return false;

        const propConfig:IPropertyConfig<any> = prop.getPropertyConfig(propPath);
        const isRequired = propConfig ? propConfig.spec.isRequired : prop.isRequired;

        const userInputString:string = userInput != null
            ? Type.isInstanceOf(userInput, Type.IntegerEnum) || Type.isInstanceOf(userInput, Type.StringEnum)
                ? JSON.stringify(serialize(userInput))
                : String(userInput)
            : null;

        const validation:ValidationResult = propConverter.validate(userInputString, isRequired);

        mk.debug(area.Events, `changePropValue:: ${propConverter.constructor.name}.validate('${userInputString}', ${isRequired}) => `, validation);

        editorState.setIsValid(validation.isValid);
        editorState.setValidationError(validation.errorMessages.join(`\n`));
        editorState.setUserInput(userInput);

        if( !validation.isValid )
            return false;

        const pendingValue = propConverter.stringToValue(userInputString, isRequired);
        editorState.setPendingValue(pendingValue);
        editorState.setIsConfirmed(false);

        mk.debug(area.Events, `changePropValue:: ${propConverter.constructor.name}.stringToValue('${userInputString}', ${isRequired}) => `, pendingValue);

        const validationResult = this._performLogicBridgeValidation(prop, propPath);
        if( !validationResult.isValid )
            return false;

        for( const result of validationResult.results )
        {
            if( !result.isValid )
                continue;

            const validationPath = result.propPath;

            const state = prop.getEditorPartState(validationPath);
            if( !state || state.isConfirmed )
                continue;

            const config = prop.getPropertyConfig(validationPath);
            if( config && !config.spec.isManualConfirmation )
                this._applyPropValue(prop, validationPath, state);
        }

        return true;
    }

    private _performLogicBridgeValidation(prop:Prop<any>, propPath:PropPath):LogicBridgeValidationResult
    {
        // currently only needed, when we change one of the user-defined props
        const isLogicBridgeValidationNeeded = !this.layoutProperties.has(prop.name) && !this.baseProperties.has(prop.name);
        if( !isLogicBridgeValidationNeeded )
            return LogicBridgeValidationResult.valid(propPath);

        const pendingValues = this.pendingValues;

        const validatedProps = [];

        // note: legacy components might check for name of a bindGroup => so for now, we have to check with all the names
        const bindGroups = prop.editorConfig && prop.editorConfig.bindGroups || {};
        for( const bindGroupName of Object.keys(bindGroups) )
        {
            const bindPath = new BindPath(bindGroupName, propPath.asKey());
            const validationResult = this._logicBridge.validate(bindPath, pendingValues);

            mk.debug(area.Events, `_performCustomValidation:: ${(this.componentConfig.logic) ? this.componentConfig.logic.name : this.componentConfig.name}.validate('${propPath.asKey()}') => `, validationResult, pendingValues);

            validationResult.fields.forEach(field =>
            {
                const fPropPath = PropPath.fromFieldPath(field.bindPath.fieldPath);

                const state = prop.getEditorPartState(fPropPath);
                state.setIsValid(field.isValid);
                state.setValidationError(field.errorMessages.join(`\n`));

                validatedProps.push(new PropPathValidationResult(fPropPath, field.isValid));
            });
        }

        return new LogicBridgeValidationResult(validatedProps);
    }

    private _applyPropValue(prop:Prop<any>, propPath:PropPath, state:EditorPartState<any>):boolean
    {
        if( !state || !state.isValid || state.isConfirmed )
            return false;

        const prevValue = prop.value;

        prop.setPropValue(propPath, state.pendingValue);

        const propBinding:PropBinding = prop.getBinding(propPath);

        if( propBinding )
            commonControllers.bindings.updateBoundSlotValue(propBinding, Repeater.default(this), state.pendingValue);

        this.propChanged(prop);

        state.clear();

        if( prop.group === PropertyGroup.Layout )
            LayoutUtils.calcConstraints(prop, prevValue, this);

        return true;
    }

    @action.bound
    public confirmPropValue(prop:Prop<any>, propPath:PropPath):boolean
    {
        mk.debug(area.Events, `confirmPropValue:: ('${prop.name}', '${propPath.asKey()}')`);

        if( !prop || !propPath )
            return false;

        const state = prop.getEditorPartState(propPath);

        this._applyPropValue(prop, propPath, state);

        mk.debug(area.Events, `confirmPropValue <= ('${prop.name}', '${propPath.asKey()}')`, prop, state);

        return true;
    }

    @action.bound
    public propChanged(prop:Prop<any>):void
    {
        mk.debug(area.Events, `propChanged :: ('${prop.name}')`, prop);

        // if( this.callbacks && this.callbacks.onPropChanged )
        //     this.callbacks.onPropChanged(prop.name);

        // console.log('%c prop =', 'background:#0f0;color:#000;', prop, editorPartState);
    }

    @action.bound
    public toggleEventState(eventProp:EventProp):boolean
    {
        eventProp.setIsEnabled(!eventProp.isEnabled);

        mk.debug(area.Events, `changeEventState:: ('${eventProp.spec.eventPropertyName}', enabled::${eventProp.isEnabled})`);

        // if( this.callbacks && this.callbacks.onEventStateChanged )
        //     this.callbacks.onEventStateChanged(eventProp.spec.eventPropertyName, eventProp.isEnabled);

        return true;
    }

    @action.bound
    public removeLinkNameToProp():boolean
    {
        this.linkNameToProp = '';

        return true;
    }

    @action.bound
    public applyAnimationFinalValueToProp(prop:Prop<any>,
                                          propPath:PropPath,
                                          userInput:UserInputValue,
                                          repeater:Repeater):boolean
    {
        if( !prop || !propPath )
            return false;

        const propConverter = prop.getPropConverter(propPath);

        if( !propConverter )
            return false;

        const propConfig:IPropertyConfig<any> = prop.getPropertyConfig(propPath);
        const isRequired = propConfig ? propConfig.spec.isRequired : prop.isRequired;

        const userInputString:string = userInput != null
            ? Type.isInstanceOf(userInput, Type.IntegerEnum) || Type.isInstanceOf(userInput, Type.StringEnum)
                ? JSON.stringify(serialize(userInput))
                : String(userInput)
            : null;

        const validation:ValidationResult = propConverter.validate(userInputString, isRequired);

        if( !validation.isValid )
            return false;

        const pendingValue = propConverter.stringToValue(userInputString, isRequired);
        const propBinding:PropBinding = prop.getBinding(propPath);
        const prevValue = prop.value;

        if( propBinding )
            commonControllers.bindings.updateBoundSlotValue(propBinding, repeater, pendingValue);

        this.propChanged(prop);

        if( prop.group === PropertyGroup.Layout )
            LayoutUtils.calcConstraints(prop, prevValue, this);

        return true;
    }

    private _getPropsValues(props:Map<PropId, Prop<any>>, repeater:Repeater):IPropsValues
    {
        const propsMap:IPropsValues = {};

        if( props )
            props.forEach((prop:Prop<any>) =>
            {
                propsMap[prop.name] = prop.getRepeatedValue(repeater);
            });

        return propsMap;
    }

    private _getPropertiesByNameMap(propertiesByIdsMap:Map<PropId, Prop<any>>):Map<PropName, Prop<any>>
    {
        const properties:Map<PropName, Prop<any>> = new Map<PropName, Prop<any>>();

        propertiesByIdsMap.forEach((prop:Prop<any>) => properties.set(prop.name, prop));

        return properties;
    }

    private static createProps(box:Box,
                               propsConfigs:Map<PropName, PropConfig<any>>,
                               group:PropertyGroup,
                               isSkipChildLayout:boolean = false):Map<PropId, Prop<any>>
    {
        const props:Map<PropId, Prop<any>> = new Map();

        propsConfigs.forEach((propConfig:PropConfig<any>, propName:PropName) =>
        {
            if( isSkipChildLayout && propName === SystemPropsConfigs.CHILD_LAYOUT_PROP_NAME )
                return;

            const prop:Prop<any> = Prop.createOf<any>(box, group, propConfig);

            props.set(prop.id, prop);
        });

        return props;
    }

    private static createEventProps(box:Box, eventMap:EventMap):Map<EventPropId, EventProp>
    {
        const eventProps:Map<EventPropId, EventProp> = new Map();

        if( eventMap )
        {
            eventMap.getEvents().forEach((eventSpec:EventSpec) =>
            {
                const eventProp:EventProp = EventProp.create(box, eventSpec.eventPropertyName, eventSpec.isEnabled);

                eventProps.set(eventProp.id, eventProp);
            });
        }

        return eventProps;
    }

    private static _propertiesToJson(properties:Map<PropId, Prop<any>>, isSkipChildLayout:boolean = false):IPropsSnapshot
    {
        return Array.from(properties.entries()).reduce((result, [propId, prop]) =>
        {
            const propSnapshot:IPropSnapshot = Prop.toJson(prop);

            if( propSnapshot && (!isSkipChildLayout || prop.name !== SystemPropsConfigs.CHILD_LAYOUT_PROP_NAME) )
                result[prop.name] = propSnapshot;

            return result;
        }, {});
    }

    private static _eventPropertiesToJson(eventProperties:Map<EventPropId, EventProp>):IEventPropsSnapshot
    {
        return Array.from(eventProperties.entries()).reduce((result, [eventPropId, eventProp]) =>
        {
            const eventPropSnapshot:IEventPropSnapshot = EventProp.toJson(eventProp);

            if( eventPropSnapshot )
                result[eventProp.name] = eventPropSnapshot;

            return result;
        }, {});
    }

    public static toJson:ITransformer<ComponentDesc, IComponentDescSnapshot> = createTransformer((componentDesc:ComponentDesc) =>
    {
        return {
            linkNameToProp: componentDesc.linkNameToProp,
            _baseProperties: ComponentDesc._propertiesToJson(componentDesc._baseProperties),
            _properties: ComponentDesc._propertiesToJson(componentDesc._properties),
            _layoutProperties: ComponentDesc._propertiesToJson(
                componentDesc._layoutProperties,
                !componentDesc.componentConfig.supportsChildren
            ),
            _baseEventProps: ComponentDesc._eventPropertiesToJson(componentDesc._baseEventProps),
            _eventProps: ComponentDesc._eventPropertiesToJson(componentDesc._eventProps)
        };
    });

    private static _propertiesFromJson(json:IPropsSnapshot,
                                       box:Box,
                                       currentState:Map<PropId, Prop<any>>,
                                       currentJson:IPropsSnapshot,
                                       isSkipChildLayout:boolean = false):Map<PropId, Prop<any>>
    {
        let properties:Map<PropId, Prop<any>>;

        if( json === currentJson )
        {
            currentState.forEach((prop:Prop<any>) => prop.box = box);

            properties = currentState;
        }
        else
        {
            properties = new Map();

            currentState.forEach((currentPropState:Prop<any>, propId:PropId) =>
            {
                const propName:PropName = currentPropState.name;

                if( isSkipChildLayout && propName === SystemPropsConfigs.CHILD_LAYOUT_PROP_NAME )
                    return;

                const propJson:IPropSnapshot = json[propName];
                const currentPropJson:IPropSnapshot = currentJson[propName];

                if( !propJson || propJson === currentPropJson )
                {
                    currentPropState.box = box;

                    if( !propJson || propJson._value === SKIP )
                        currentPropState.setInnerValue(currentPropState.propConfig.defaultValue);

                    properties.set(currentPropState.id, currentPropState);
                }
                else
                {
                    const prop = Prop.fromJson(propJson, box, currentPropState, currentPropJson);
                    properties.set(prop.id, prop);
                }
            });
        }

        return properties;
    }

    private static _eventPropertiesFromJson(json:IEventPropsSnapshot,
                                            box:Box,
                                            currentState:Map<EventPropId, EventProp>,
                                            currentJson:IEventPropsSnapshot):Map<EventPropId, EventProp>
    {
        let eventProps:Map<EventPropId, EventProp>;

        if( json === currentJson )
        {
            currentState.forEach((baseEventProp:EventProp) => baseEventProp.box = box);

            eventProps = currentState;
        }
        else
        {
            eventProps = new Map();

            currentState.forEach((currentEventPropState:EventProp, eventPropId:EventPropId) =>
            {
                const eventPropName:EventPropName = currentEventPropState.name;
                const eventPropJson:IEventPropSnapshot = json[eventPropName];
                const currentEventPropJson:IEventPropSnapshot = currentJson[eventPropName];

                if( !eventPropJson || eventPropJson === currentEventPropJson )
                {
                    currentEventPropState.box = box;

                    if( !eventPropJson )
                        currentEventPropState.clearBindings();

                    eventProps.set(eventPropId, currentEventPropState);
                }
                else
                {
                    const eventProp = EventProp.fromJson(eventPropJson, box);
                    eventProps.set(eventProp.id, eventProp);
                }
            });
        }

        return eventProps;
    }

    public static fromJson(json:IComponentDescSnapshot,
                           box:Box,
                           currentState?:ComponentDesc,
                           currentJson?:IComponentDescSnapshot,
                           ctx?:DesignStateFromJsonCtx):ComponentDesc
    {
        if( !currentState )
            return ComponentDesc.fromJsonDeep(json, box);

        if( !currentJson )
            currentJson = ComponentDesc.toJson(currentState);

        const componentDesc:ComponentDesc = new ComponentDesc();

        componentDesc.box = box;
        componentDesc.linkNameToProp = json.linkNameToProp;

        componentDesc._baseProperties = ComponentDesc._propertiesFromJson(
            json._baseProperties,
            box,
            currentState._baseProperties,
            currentJson._baseProperties
        );
        componentDesc._properties = ComponentDesc._propertiesFromJson(
            json._properties,
            box,
            currentState._properties,
            currentJson._properties
        );
        componentDesc._layoutProperties = ComponentDesc._propertiesFromJson(
            json._layoutProperties,
            box,
            currentState._layoutProperties,
            currentJson._layoutProperties,
            !componentDesc.componentConfig.supportsChildren
        );

        componentDesc._baseEventProps = ComponentDesc._eventPropertiesFromJson(
            json._baseEventProps,
            box,
            currentState._baseEventProps,
            currentJson._baseEventProps
        );
        componentDesc._eventProps = ComponentDesc._eventPropertiesFromJson(
            json._eventProps,
            box,
            currentState._eventProps,
            currentJson._eventProps
        );

        if( componentDesc._logicBridge !== currentState._logicBridge )
            componentDesc._logicBridge = new LogicBridge(componentDesc);

        return componentDesc;
    }

    private static _propertiesFromJsonDeep(propsSnapshot:IPropsSnapshot,
                                           box:Box,
                                           propsConfigs:Map<PropName, PropConfig<any>>,
                                           group:PropertyGroup,
                                           isSkipChildLayout:boolean = false):Map<PropId, Prop<any>>
    {
        const properties:Map<PropId, Prop<any>> = new Map();

        propsConfigs.forEach((propConfig:PropConfig<any>, propName:PropName) =>
        {
            if( isSkipChildLayout && propName === SystemPropsConfigs.CHILD_LAYOUT_PROP_NAME )
                return;

            const propSnapshot:IPropSnapshot = propsSnapshot[propName];

            const prop:Prop<any> = propSnapshot
                ? Prop.fromJsonDeep(propSnapshot, box)
                : Prop.createOf<any>(box, group, propConfig);

            properties.set(prop.id, prop);
        });

        return properties;
    }

    private static _eventPropertiesFromJsonDeep(eventPropsSnapshot:IEventPropsSnapshot,
                                                box:Box,
                                                eventMap:EventMap):Map<EventPropId, EventProp>
    {
        const eventProps:Map<EventPropId, EventProp> = new Map();

        if( eventMap )
        {
            eventMap.getEvents().forEach((eventSpec:EventSpec) =>
            {
                const eventPropSnapshot:IEventPropSnapshot = eventPropsSnapshot[eventSpec.eventPropertyName];

                const eventProp:EventProp = eventPropSnapshot
                    ? EventProp.fromJsonDeep(eventPropSnapshot, box)
                    : EventProp.create(box, eventSpec.eventPropertyName, eventSpec.isEnabled);

                eventProps.set(eventProp.id, eventProp);
            });
        }

        return eventProps;
    }

    public static fromJsonDeep(json:IComponentDescSnapshot, box:Box):ComponentDesc
    {
        const {resolvedComponent} = box.component.ref;
        const componentDesc:ComponentDesc = new ComponentDesc();

        componentDesc.box = box;
        componentDesc.linkNameToProp = json.linkNameToProp;

        componentDesc._baseProperties = ComponentDesc._propertiesFromJsonDeep(
            json._baseProperties,
            box,
            SystemPropsConfigs.basePropertiesConfigs,
            PropertyGroup.Base
        );
        componentDesc._properties = ComponentDesc._propertiesFromJsonDeep(
            json._properties,
            box,
            resolvedComponent.propertiesConfigs,
            PropertyGroup.Component
        );
        componentDesc._layoutProperties = ComponentDesc._propertiesFromJsonDeep(
            json._layoutProperties,
            box,
            resolvedComponent.layoutPropertiesConfigs,
            PropertyGroup.Layout,
            !componentDesc.componentConfig.supportsChildren
        );

        componentDesc._baseEventProps = ComponentDesc._eventPropertiesFromJsonDeep(
            json._baseEventProps,
            box,
            SystemPropsConfigs.baseEventsMap
        );
        componentDesc._eventProps = ComponentDesc._eventPropertiesFromJsonDeep(
            json._eventProps,
            box,
            resolvedComponent.eventMap
        );

        componentDesc._logicBridge = new LogicBridge(componentDesc);

        return componentDesc;
    }
}
